import { router } from '@inertiajs/react'
import { useMemo, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Switch } from '@/components/ui/Switch'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/Select'
import { IconPicker } from '@/components/ui/IconPicker'
import { CoverImagePicker } from '@/components/help_center/CoverImagePicker'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { HelpCategoryNode } from '@/pages/HelpCenter/types'

interface CategoryFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  category?: HelpCategoryNode | null
  categories: HelpCategoryNode[]
  brandPrimary: string | null
  brandAccent: string | null
}

const NO_PARENT = '__none__'

export function CategoryFormDialog({ open, onOpenChange, category, categories, brandPrimary, brandAccent }: CategoryFormDialogProps) {
  const { withAccount } = useRouteParams()
  const editing = Boolean(category)
  const [name, setName] = useState(category?.name ?? '')
  const [description, setDescription] = useState(category?.description ?? '')
  const [icon, setIcon] = useState(category?.icon ?? '')
  const [parentId, setParentId] = useState(category?.parent_id ?? NO_PARENT)
  const [published, setPublished] = useState(category?.published ?? false)
  const [section, setSection] = useState(category?.section ?? '')

  // Existing section labels to suggest (top-level categories only).
  const sectionOptions = useMemo(
    () => [...new Set(categories.map(c => c.section).filter((s): s is string => Boolean(s)))],
    [categories],
  )

  // Can't parent a category under itself or any of its descendants — offering
  // those would just bounce off the server-side cycle validation.
  const excludedIds = useMemo(() => {
    if (!category) return new Set<string>()
    const ids = new Set<string>([category.id])
    let grew = true
    while (grew) {
      grew = false
      for (const c of categories) {
        if (c.parent_id && ids.has(c.parent_id) && !ids.has(c.id)) {
          ids.add(c.id)
          grew = true
        }
      }
    }
    return ids
  }, [category, categories])

  const parentOptions = categories.filter(c => !excludedIds.has(c.id))

  const submit = () => {
    const payload = {
      help_category: {
        name,
        description,
        icon,
        parent_id: parentId === NO_PARENT ? null : parentId,
        published,
        section: parentId === NO_PARENT ? section : '',
      },
    }
    const opts = { preserveScroll: true, onSuccess: () => onOpenChange(false) }

    if (editing && category) {
      router.patch(routes.helpCategories.update.path(withAccount({ id: category.slug })), payload, opts)
    } else {
      router.post(routes.helpCategories.create.path(withAccount()), payload, opts)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit category' : 'New category'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Getting started" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-2">
            <Label>Icon</Label>
            <IconPicker value={icon} onChange={setIcon} />
          </div>
          <div className="space-y-2">
            <Label>Parent category</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger><SelectValue placeholder="Top level" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PARENT}>Top level</SelectItem>
                {parentOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {parentId === NO_PARENT && (
            <div className="space-y-2">
              <Label>Section</Label>
              <Input
                value={section}
                onChange={e => setSection(e.target.value)}
                placeholder="Optional group heading, e.g. Apps & add-ins"
                list="hc-section-options"
              />
              <datalist id="hc-section-options">
                {sectionOptions.map(s => <option key={s} value={s} />)}
              </datalist>
              <p className="text-xs text-muted-foreground">Groups top-level categories under a heading in the sidebar and browse list.</p>
            </div>
          )}
          <div className="flex items-center justify-between">
            <Label>Published</Label>
            <Switch checked={published} onCheckedChange={setPublished} />
          </div>
          {category?.id
            ? (
              <div className="space-y-2">
                <Label>Cover</Label>
                <p className="text-xs text-muted-foreground">Optional cover shown on the category card.</p>
                <CoverImagePicker
                  resource="help_category"
                  id={category.id}
                  currentUrl={category.feature_image_url}
                  cover={category.cover ?? {}}
                  brandPrimary={brandPrimary}
                  brandAccent={brandAccent}
                  defaultIcon={category?.icon}
                />
              </div>
            )
            : <p className="text-xs text-muted-foreground">Save the category first to add a cover.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!name.trim()}>{editing ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
